import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { apiFetch } from "@/lib/api-client";
import { useTopbar } from "@/components/chrome/use-topbar";
import { formatDate } from "@/lib/format";
import { MarketFilterDropdown } from "@/components/markets/MarketFilterDropdown";
import { marketIdsQuery } from "@/lib/market-filter";
import { meProfileQueryOptions } from "@/lib/me-profile";

interface Run {
  id: string;
  runId: string;
  startedAt: number;
  finishedAt: number | null;
  status: "running" | "ok" | "partial" | "failed";
  trigger: "cron" | "manual";
  triggeredByEmail: string | null;
  lookbackDays: number;
  newCandidates: number;
  // populated by extended /api/runs (Task 9)
  priorityACount: number;
  priorityBCount: number;
  mailedCount: number;
  errorSummary: string | null;
}

export const Route = createFileRoute("/_app/history")({
  component: HistoryPage,
});

const STATUS_LABEL: Record<Run["status"], string> = {
  running: "Running",
  ok: "Complete",
  partial: "Partial",
  failed: "Failed",
};
const STATUS_STYLE: Record<Run["status"], string> = {
  running: "bg-paper-2 text-muted",
  ok: "bg-pos-bg text-pos",
  partial: "bg-warn-bg text-warn",
  failed: "bg-neg-bg text-neg",
};

function HistoryPage() {
  const { data: profile } = useQuery(meProfileQueryOptions);
  const marketIds = profile?.opportunitiesMarketIds ?? null;
  const { data, isLoading } = useQuery<{ rows: Run[] }>({
    queryKey: ["runs", marketIds],
    queryFn: () => {
      const q = marketIdsQuery(marketIds);
      const suffix = q ? `?${q.replace(/^&/, "")}` : "";
      return apiFetch<{ rows: Run[] }>(`/api/runs${suffix}`);
    },
  });

  const rows = data?.rows ?? [];

  const { setTopbar } = useTopbar();
  useEffect(() => {
    setTopbar({
      crumbs: [{ label: "Operations" }, { label: "History" }],
      rightSlot: null,
    });
    return () => setTopbar({ crumbs: [], rightSlot: null });
  }, [setTopbar]);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-6 border-b border-rule pb-5">
        <div>
          <h1 className="mb-1 text-3xl font-semibold tracking-tight text-ink">Run history</h1>
          <p className="text-sm text-muted">
            Review past candidate scans, queue volume, and mail progress.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <MarketFilterDropdown />
          <button
            type="button"
            disabled
            title="Coming soon"
            className="rounded-md border border-rule-strong bg-surface px-3.5 py-2 text-xs font-semibold text-ink opacity-60"
          >
            Export history
          </button>
        </div>
      </div>

      <section className="overflow-hidden rounded-lg border border-rule bg-surface">
        <header className="flex items-center justify-between border-b border-rule px-5 py-4">
          <h2 className="text-[13px] font-semibold uppercase tracking-wider text-ink">
            Recent scans
          </h2>
        </header>
        {isLoading ? (
          <p className="px-5 py-6 text-sm text-muted">Loading…</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead className="border-b border-rule bg-paper text-left">
                <tr>
                  <th className="px-5 py-3 text-[10px] uppercase tracking-wider text-muted">
                    Date
                  </th>
                  <th className="px-5 py-3 text-[10px] uppercase tracking-wider text-muted">
                    Window
                  </th>
                  <th className="px-5 py-3 text-[10px] uppercase tracking-wider text-muted">
                    Candidates
                  </th>
                  <th className="px-5 py-3 text-[10px] uppercase tracking-wider text-muted">
                    Priority A
                  </th>
                  <th className="px-5 py-3 text-[10px] uppercase tracking-wider text-muted">
                    Priority B
                  </th>
                  <th className="px-5 py-3 text-[10px] uppercase tracking-wider text-muted">
                    Mailed
                  </th>
                  <th className="px-5 py-3 text-[10px] uppercase tracking-wider text-muted">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.runId} className="border-b border-rule align-top">
                    <td className="px-5 py-3.5">{formatDate(r.startedAt)}</td>
                    <td className="px-5 py-3.5">{r.lookbackDays}d</td>
                    <td className="px-5 py-3.5">{r.newCandidates}</td>
                    <td className="px-5 py-3.5">{r.priorityACount}</td>
                    <td className="px-5 py-3.5">{r.priorityBCount}</td>
                    <td className="px-5 py-3.5">{r.mailedCount}</td>
                    <td className="px-5 py-3.5">
                      <span
                        className={`inline-flex rounded px-2 py-1 text-[11px] font-bold uppercase tracking-wider ${STATUS_STYLE[r.status]}`}
                        title={r.errorSummary ?? undefined}
                      >
                        {STATUS_LABEL[r.status]}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <p className="mt-2 border-l-2 border-tan-dark bg-paper-2 px-5 py-3.5 text-xs leading-relaxed text-muted">
        <strong className="text-ink">History records discovery results.</strong> PDFs are generated
        on demand, so opened counts reflect user activity rather than precomputed batch output.
      </p>
    </div>
  );
}
